import type { DeepKeys } from "@tanstack/table-core";
import get from "lodash/get";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";
import { useMemo, useState } from "react";

import { QuestionWarningAlert } from "./question-warning-alert";
import { AlertSeverity } from "@/types/utils";

interface PostPublishQuestionEditWarningsProps<T extends object> {
  enabled: boolean;
  value: T;
  warningMap: Map<DeepKeys<T>, string>;
}
const HELP_HREF =
  "https://intercom.help/upwage-support/en/articles/12677618-editing-live-ai-interviewers";

export const PostPublishQuestionEditWarnings = <T extends object>({
  enabled,
  value,
  warningMap,
}: PostPublishQuestionEditWarningsProps<T>) => {
  const [initialValue] = useState(value);
  const activeWarnings = useMemo(
    () =>
      uniq(
        warningMap.keys().reduce<string[]>((warnings, key) => {
          const warning = warningMap.get(key);

          if (warning && !isEqual(get(value, key), get(initialValue, key))) {
            return warnings.concat(warning);
          }

          return warnings;
        }, []),
      ),
    [initialValue, value, warningMap],
  );

  if (!enabled) {
    return null;
  }

  return (
    <QuestionWarningAlert
      warning={activeWarnings.map((message) => ({
        helpHref: HELP_HREF,
        message,
        severity: AlertSeverity.Warning,
      }))}
    />
  );
};
